import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ArrowDown, ArrowLeft, ArrowUp, Check, FileText, Loader2, Minus, Plus, TrendingUp, Upload, X } from 'lucide-react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { taxService } from '../../../services/inventory.service'
import { paymentMethodService, PaymentMethodItem } from '../../../services/payment-method.service'
import {
  PurchaseXmlDecision,
  PurchaseXmlPreview,
  PurchaseXmlPreviewRow,
  purchasesService,
} from '../../../services/purchases.service'
import { supplierService, SupplierItem } from '../../../services/supplier.service'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { SatKeySelect } from '../../ui/SatKeySelect'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog'
import { Input } from '../../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { usePurchaseBranches } from '../use-purchase-branches'
import { PurchaseBatchSelect } from './PurchaseBatchSelect'
import { useSessionStore } from '../../../store/auth/auth.store'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}

interface LocalDecision extends Omit<PurchaseXmlDecision, 'resolution'> {
  resolution?: PurchaseXmlDecision['resolution']
  requires_lot?: boolean
}

interface ProductOption {
  id: number
  name: string
  barcode: string
  sku: string | null
  current_stock: number | null
  product_type: { id: number; name: string; requires_lot: boolean } | null
}

interface TaxItem { id: number; name: string; percentage: string | number }

function unwrapList<T>(response: unknown): T[] {
  if (!response || typeof response !== 'object' || !('data' in response)) return []
  const data = (response as { data?: unknown }).data
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object' && 'data' in data) {
    const nested = (data as { data?: unknown }).data
    return Array.isArray(nested) ? nested : []
  }
  return []
}

const money = (value: string | null, currency = 'MXN') => value === null
  ? 'Sin capturar'
  : new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value))

function initialDecision(row: PurchaseXmlPreviewRow): LocalDecision {
  if (row.status === 'existing_product') {
    return {
      row_key: row.row_key,
      resolution: 'existing_product',
      requires_lot: row.resolved_product?.product_type?.requires_lot,
      batch_mode: row.resolved_product?.product_type?.requires_lot ? 'new' : undefined,
    }
  }
  return {
    row_key: row.row_key,
    name: row.name,
    description: row.description,
    barcode: row.identifier || '',
    sat_product_key_code: row.sat_product_key_code || undefined,
    sat_product_key_name: row.resolved_catalogs.sat_key?.name,
    unit_id: row.resolved_catalogs.unit?.id,
    tax_id: row.suggested_tax?.id,
  }
}

export function XmlEntryWizardModal({ open, onOpenChange, onCreated }: Props) {
  const today = new Date().toISOString().slice(0, 10)
  const inputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<1 | 2>(1)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<PurchaseXmlPreview | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [paymentCondition, setPaymentCondition] = useState<'cash' | 'credit'>('cash')
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [entryDate, setEntryDate] = useState(today)
  const [acknowledged, setAcknowledged] = useState<string[]>([])
  const [decisions, setDecisions] = useState<Record<string, LocalDecision>>({})
  const [productSearch, setProductSearch] = useState('')
  const [sucursalId, setSucursalId] = useState('')
  const branchesQuery = usePurchaseBranches(open)
  const sessionStore = useSessionStore()
  const activeBranchId = sessionStore.activeBranchId
    ? String(sessionStore.activeBranchId)
    : ''

  const suppliersQuery = useQuery({
    queryKey: ['purchase-suppliers', 'active'],
    queryFn: () => supplierService.getItems({ all: true, is_active: true }),
    enabled: open,
  })
  const paymentsQuery = useQuery({
    queryKey: ['purchase-payment-methods', 'active'],
    queryFn: () => paymentMethodService.getItems({ all: true, is_active: true }),
    enabled: open,
  })
  const taxesQuery = useQuery({
    queryKey: ['taxes', 'all'],
    queryFn: () => taxService.getTaxes({ all: true }),
    enabled: open,
  })
  const optionsQuery = useQuery({
    queryKey: ['purchase-product-options', sucursalId, productSearch],
    queryFn: () => purchasesService.getPurchaseProductOptions({ sucursal_id: Number(sucursalId), search: productSearch, per_page: 30 }),
    enabled: open && step === 2 && Boolean(sucursalId) && productSearch.trim().length >= 2,
  })

  const suppliers = useMemo(() => unwrapList<SupplierItem>(suppliersQuery.data), [suppliersQuery.data])
  const payments = useMemo(() => unwrapList<PaymentMethodItem>(paymentsQuery.data), [paymentsQuery.data])
  const taxes = useMemo(() => unwrapList<TaxItem>(taxesQuery.data), [taxesQuery.data])
  const productOptions = useMemo(() => unwrapList<ProductOption>(optionsQuery.data), [optionsQuery.data])
  const branches = branchesQuery.data ?? []
  const destination = branches.find((branch) => String(branch.id) === sucursalId)
  const supplier = suppliers.find((item) => String(item.id) === supplierId)
  const supplierMismatch = Boolean(supplier?.rfc && preview && supplier.rfc.toUpperCase() !== preview.cfdi.issuer.rfc.toUpperCase())
  const warningCodes = useMemo(() => [
    ...(preview?.warnings.filter((warning) => warning.requires_acknowledgement).map((warning) => warning.code) || []),
    ...((supplier && !supplier.rfc) ? ['supplier_without_rfc'] : []),
  ], [preview, supplier])
  const warningsAccepted = warningCodes.every((code) => acknowledged.includes(code))

  useEffect(() => {
    if (open) return
    setStep(1); setFile(null); setPreview(null); setError(''); setSupplierId('')
    setPaymentCondition('cash'); setPaymentMethodId(''); setEntryDate(today)
    setAcknowledged([]); setDecisions({}); setProductSearch(''); setSucursalId(activeBranchId)
  }, [open, today, activeBranchId])

  const analyze = async (selected: File, branchId: number, preserveInternalData = false) => {
    setFile(selected); setAnalyzing(true); setError(''); setPreview(null)
    setDecisions({}); setProductSearch('')
    try {
      const response = await purchasesService.previewXml(selected, branchId)
      const result = response.data
      setFile(selected); setPreview(result)
      if (!preserveInternalData) {
        setAcknowledged([])
        setSupplierId(result.suggestions.supplier && !result.suggestions.supplier.blocked ? String(result.suggestions.supplier.id) : '')
        setPaymentCondition(result.suggestions.payment_condition)
        setPaymentMethodId(result.suggestions.payment_method ? String(result.suggestions.payment_method.id) : '')
      }
      setDecisions(Object.fromEntries(result.rows.map((row) => [row.row_key, initialDecision(row)])))
      if (preserveInternalData) {
        toast.info('El CFDI se analizó nuevamente para la sucursal seleccionada. Revisa las partidas.')
      }
    } catch (caught) {
      const message = caught && typeof caught === 'object' && 'response' in caught
        ? (caught as { response?: { data?: { message?: string; errors?: { file?: string[] } } } }).response?.data
        : undefined
      setError(message?.errors?.file?.[0] || message?.message || 'No fue posible validar el CFDI.')
    } finally {
      setAnalyzing(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    if (selected && sucursalId) void analyze(selected, Number(sucursalId))
  }

  const changeDestination = (value: string) => {
    const changed = value !== sucursalId
    setSucursalId(value)

    if (changed && file) {
      void analyze(file, Number(value), true)
    }
  }

  const updateDecision = (rowKey: string, values: Partial<LocalDecision>) => {
    setDecisions((current) => ({ ...current, [rowKey]: { ...current[rowKey], ...values } }))
  }

  const canContinue = Boolean(sucursalId && preview && supplierId && paymentMethodId && entryDate && warningsAccepted && !supplierMismatch && !preview.suggestions.supplier?.blocked && preview.summary.blocked === 0 && !analyzing)
  const invalidRows = preview?.rows.some((row) => {
    const decision = decisions[row.row_key]
    if (!decision?.resolution || row.status === 'blocked') return true
    if (decision.resolution === 'non_inventory') return false
    if (decision.resolution === 'existing_product' && row.status !== 'existing_product' && !decision.product_id) return true
    const requiresLot = Boolean(decision.requires_lot || row.resolved_product?.product_type?.requires_lot)
    if (requiresLot && (
      !decision.batch_mode
      || (decision.batch_mode === 'existing' && !decision.product_batch_id)
      || (decision.batch_mode === 'new' && (!decision.lot_number || !decision.expiration_date))
    )) return true
    if (decision.resolution === 'existing_product') return false
    return !decision.name || !decision.description || !decision.barcode || !decision.category_id
      || !decision.department_id || !decision.product_type_id || !decision.unit_id
  }) ?? true

  const confirm = async () => {
    if (!file || !preview || !canContinue || invalidRows) return
    setSubmitting(true); setError('')
    try {
      await purchasesService.createXmlPurchase({
        sucursal_id: Number(sucursalId),
        supplier_id: Number(supplierId), entry_date: entryDate, payment_condition: paymentCondition,
        payment_method_id: Number(paymentMethodId), acknowledged_warnings: acknowledged,
        items: preview.rows.map((row) => {
          const decision = { ...decisions[row.row_key] }
          delete decision.requires_lot
          return decision as PurchaseXmlDecision
        }),
      }, file)
      toast.success(`Ingreso CFDI registrado en ${destination?.name || 'la sucursal seleccionada'}.`)
      onOpenChange(false)
      onCreated()
    } catch (caught) {
      const response = caught && typeof caught === 'object' && 'response' in caught
        ? (caught as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response?.data
        : undefined
      const firstError = response?.errors ? Object.values(response.errors).flat()[0] : undefined
      setError(firstError || response?.message || 'No fue posible registrar el ingreso CFDI.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92dvh] min-w-0 flex-col overflow-hidden sm:max-w-6xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>{step === 1 ? 'Cargar factura CFDI XML' : 'Revisión de partidas y costos'}</DialogTitle>
          <DialogDescription>
            {step === 1 ? 'Validación fiscal y datos internos del ingreso.' : 'Los costos del CFDI quedan pendientes para Ajustes de Precio.'}
          </DialogDescription>
        </DialogHeader>

        <div data-slot="xml-entry-wizard-body" className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          {step === 1 ? (
            <div className="min-w-0 space-y-5">
            <Field label="Sucursal destino *">
              <Select value={sucursalId} onValueChange={changeDestination} disabled={branchesQuery.isLoading || branchesQuery.isError || analyzing}>
                <SelectTrigger><SelectValue placeholder={branchesQuery.isLoading ? 'Cargando sucursales...' : 'Seleccionar sucursal destino'} /></SelectTrigger>
                <SelectContent>{branches.map((branch) => <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs font-normal text-card-description">Las existencias, lotes y movimientos se registrarán en esta sucursal.</p>
              {branchesQuery.isError && <p className="text-sm font-normal text-red-600">No fue posible cargar las sucursales disponibles.</p>}
              {!branchesQuery.isLoading && !branchesQuery.isError && branches.length === 0 && <p className="text-sm font-normal text-red-600">No tienes sucursales activas disponibles.</p>}
            </Field>
            <div className="rounded-lg border-2 border-dashed p-6 text-center">
              <input ref={inputRef} id="cfdi-upload" type="file" accept=".xml,application/xml,text/xml" className="hidden" onChange={selectFile} disabled={!sucursalId || analyzing || branchesQuery.isLoading || branchesQuery.isError} />
              {analyzing ? <><Loader2 className="mx-auto h-9 w-9 animate-spin text-primary" /><p className="mt-2 text-sm">Validando esquema, timbre y sellos...</p></> : (
                <label htmlFor={sucursalId ? 'cfdi-upload' : undefined} className={sucursalId ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}>
                  <Upload className="mx-auto h-10 w-10 text-card-description" />
                  <p className="mt-2 font-medium">{file?.name || 'Seleccionar Factura XML'}</p>
                  <p className="text-xs text-card-description">{sucursalId ? 'XML con formato CFDI 3.3 o 4.0' : 'Selecciona primero la sucursal destino'}</p>
                </label>
              )}
            </div>

            {!file && <div className="rounded-lg border border-border bg-card p-4">
              <p className="mb-2 text-sm font-medium text-card-foreground">Información:</p>
              <ul className="list-inside list-disc space-y-1 text-sm text-card-description">
                <li>El sistema procesará automáticamente los productos del XML</li>
                <li>Se validará que los productos existan en el catálogo</li>
                <li>Al confirmar, se actualizará el inventario con las cantidades ingresadas</li>
                <li>Se generará un registro de compra con todos los detalles</li>
              </ul>
            </div>}

            {preview && (
              <>
                <div className="grid gap-x-4 gap-y-5 rounded-xl border border-border bg-card p-5 md:grid-cols-4">
                  <Info label="Sucursal destino" value={destination?.name || 'Sin seleccionar'} />
                  <Info label="CFDI" value={`${preview.cfdi.version} · ${preview.cfdi.series ? `${preview.cfdi.series}-` : ''}${preview.cfdi.folio || preview.cfdi.uuid}`} />
                  <Info className="md:col-span-2" label="Emisor" value={`${preview.cfdi.issuer.name} · ${preview.cfdi.issuer.rfc}`} />

                  <Info label="Total fiscal" value={money(preview.cfdi.total, preview.cfdi.currency)} />
                  <Info label="Fecha" value={preview.cfdi.issued_at.replace('T', ' ')} />
                  <Info className="md:col-span-2" label="Receptor" value={`${preview.cfdi.receiver.name} · ${preview.cfdi.receiver.rfc}`} />

                  <Info label="Conceptos" value={String(preview.summary.total)} />
                  <Info label="Pago SAT" value={`${preview.cfdi.sat_payment_method_code || '—'} / ${preview.cfdi.sat_payment_form_code || '—'}`} />
                  <Info className="md:col-span-2" label="UUID" value={preview.cfdi.uuid} />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Proveedor">
                    <Select value={supplierId} onValueChange={(value) => { setSupplierId(value); setAcknowledged((codes) => codes.filter((code) => code !== 'supplier_without_rfc')) }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar proveedor activo" /></SelectTrigger>
                      <SelectContent>{suppliers.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}{item.rfc ? ` · ${item.rfc}` : ''}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Condición de pago">
                    <Select value={paymentCondition} onValueChange={(value: 'cash' | 'credit') => setPaymentCondition(value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Contado</SelectItem><SelectItem value="credit">Crédito</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <Field label="Método de pago interno">
                    <Select value={paymentMethodId} onValueChange={setPaymentMethodId}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar método" /></SelectTrigger>
                      <SelectContent>{payments.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Fecha de ingreso"><Input type="date" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} /></Field>
                </div>

                {supplierMismatch && <Alert text="El RFC del proveedor seleccionado no coincide con el emisor del CFDI." danger />}
                {preview.suggestions.supplier?.blocked && <Alert text="El proveedor que coincide por RFC está inactivo; reactívalo desde Proveedores." danger />}
                {preview.warnings.map((warning) => <WarningCheck key={warning.code} code={warning.code} text={warning.message} checked={acknowledged.includes(warning.code)} onChange={(checked) => setAcknowledged((codes) => checked ? [...new Set([...codes, warning.code])] : codes.filter((code) => code !== warning.code))} />)}
                {supplier && !supplier.rfc && <WarningCheck code="supplier_without_rfc" text="El proveedor no tiene RFC capturado. Confirmo que corresponde al emisor de este CFDI." checked={acknowledged.includes('supplier_without_rfc')} onChange={(checked) => setAcknowledged((codes) => checked ? [...new Set([...codes, 'supplier_without_rfc'])] : codes.filter((code) => code !== 'supplier_without_rfc'))} />}
              </>
            )}
            {error && file && sucursalId && !analyzing && <Button type="button" variant="outline" onClick={() => void analyze(file, Number(sucursalId), true)}>Reintentar análisis</Button>}
            </div>
          ) : preview && (
            <div className="min-w-0 space-y-6">
            <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-5 w-5 text-primary" />
                <div className="flex-1">
                  <h3 className="mb-1 font-medium text-card-foreground"> {preview.summary.total} productos encontrados en el XML</h3>
                  <p className="mb-1 text-sm text-card-foreground"> Proveedor:{' '}<span className="font-semibold">{supplier?.name || preview.cfdi.issuer.name}</span></p>
                  <p className="mb-3 text-sm text-card-foreground"> Sucursal destino:{' '}<span className="font-semibold">{destination?.name || 'Sin seleccionar'}</span></p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <ReviewCount label="Incrementos" value={preview.summary.increases} />
                    <ReviewCount label="Decrementos" value={preview.summary.decreases} />
                    <ReviewCount label="Sin cambio" value={preview.summary.unchanged} />
                  </div>
                  {(preview.summary.unmatched > 0 || preview.summary.blocked > 0 || preview.summary.without_previous_cost > 0) && (
                    <p className="mt-3 text-xs text-primary">Sin conciliar: {preview.summary.unmatched} · Bloqueados: {preview.summary.blocked} · Sin costo anterior: {preview.summary.without_previous_cost}</p>
                  )}
                </div>
              </div>
            </div>

            <div data-slot="xml-cost-table-scroll" className="min-w-0 max-w-full overflow-x-auto rounded-lg border border-table-border">
              <table className="w-full min-w-[1120px]">
                <thead className="border-b border-table-border bg-table-header-bg">
                  <tr className="text-left text-xs font-medium uppercase text-table-header-foreground">
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3">Cant.</th>
                    <th className="px-4 py-3">Costo anterior</th>
                    <th className="px-4 py-3">Costo CFDI neto</th>
                    <th className="px-4 py-3">Cambio</th>
                    <th className="px-4 py-3">Precio de Venta</th>
                    <th className="px-4 py-3">Utilidad %</th>
                    <th className="px-4 py-3">Lote</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {preview.rows.map((row) => {
                    const decision = decisions[row.row_key]
                    const requiresLot = Boolean(decision?.requires_lot || row.resolved_product?.product_type?.requires_lot)
                    return (
                      <tr key={row.row_key} className={row.status === 'blocked' ? 'bg-red-50' : 'hover:bg-table-row-bg'}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2"><p className="text-sm font-medium text-table-foreground">{row.name}</p>{row.status === 'existing_product' && <Badge className="bg-emerald-100 text-emerald-700">Existente</Badge>}{row.status === 'unmatched' && <Badge className="bg-blue-100 text-blue-700">Sin conciliar</Badge>}{row.status === 'blocked' && <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>}</div>
                          <p className="mt-1 font-mono text-xs text-card-description">{row.identifier || 'Sin código de barras'}</p><p className="text-xs text-card-description">SAT: {row.sat_product_key_code || 'Sin capturar'}</p>
                          {row.errors.map((message) => <p key={message} className="mt-1 text-xs text-red-700">{message}</p>)}
                        </td>
                        <td className="px-4 py-3 font-medium text-table-foreground">{row.quantity}</td>
                        <td className="px-4 py-3 text-table-header-foreground">{money(row.cost_comparison.previous_cost)}</td>
                        <td className="px-4 py-3 font-semibold text-table-foreground">{money(row.net_unit_cost)}</td>
                        <td className="px-4 py-3"><CostChange row={row} /></td>
                        <td className="px-4 py-3 font-medium text-table-foreground">{money(row.resolved_product?.price?.final_price ?? null)}</td>
                        <td className="px-4 py-3 font-medium text-table-foreground">{row.resolved_product?.price ? `${row.resolved_product.price.profit_percentage}%` : 'Sin capturar'}</td>
                        <td className="px-4 py-3 text-xs text-table-foreground/80">
                          {decision?.resolution === 'non_inventory' || !requiresLot
                            ? 'No aplica'
                            : decision?.batch_mode === 'existing'
                              ? (decision.product_batch_id ? `Existente #${decision.product_batch_id}` : 'Pendiente')
                              : decision?.lot_number ? `${decision.lot_number}${decision.expiration_date ? ` · ${decision.expiration_date}` : ''}` : 'Pendiente'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {preview.rows.some((row) => row.status === 'unmatched') && (
              <div className="min-w-0 space-y-4">
                <div className="rounded-lg border border-blue-400/50 bg-blue-500/10 p-4">
                  <div className="flex items-start gap-3"><Plus className="mt-0.5 h-5 w-5 text-blue-600" /><div><h4 className="font-medium text-blue-300">{preview.summary.unmatched} producto{preview.summary.unmatched === 1 ? '' : 's'} no conciliado{preview.summary.unmatched === 1 ? '' : 's'}</h4><p className="mt-1 text-sm text-blue-300/80">Vincula un producto, registra uno nuevo o conserva el concepto como no inventariable.</p></div></div>
                </div>
                {preview.rows.filter((row) => row.status === 'unmatched').map((row) => {
                  const decision = decisions[row.row_key]
                  const requiresLot = Boolean(decision?.requires_lot)
                  return (
                    <div key={row.row_key} className="min-w-0 rounded-lg border-2 border-blue-400/60 bg-card p-4">
                      <div className="mb-4 flex items-center gap-2"><Badge variant="outline" className="border-blue-300 bg-blue-100 text-blue-700">Partida {row.row_number}</Badge><h5 className="font-medium text-card-foreground">{row.name}</h5></div>
                      <Field label="Tratamiento de inventario">
                        <Select value={decision?.resolution || ''} onValueChange={(value: PurchaseXmlDecision['resolution']) => updateDecision(row.row_key, { resolution: value, product_id: undefined, requires_lot: false, batch_mode: undefined, product_batch_id: undefined, lot_number: undefined, expiration_date: undefined })}>
                          <SelectTrigger><SelectValue placeholder="Elegir una opción" /></SelectTrigger><SelectContent><SelectItem value="new_product">Registrar producto nuevo</SelectItem><SelectItem value="existing_product">Vincular producto existente</SelectItem><SelectItem value="non_inventory">No inventariable</SelectItem></SelectContent>
                        </Select>
                      </Field>
                      {decision?.resolution === 'existing_product' && (
                        <div className="mt-3 space-y-2"><Input value={productSearch} onChange={(event) => setProductSearch(event.target.value)} placeholder="Buscar por nombre, código o SKU..." />
                          <Select value={decision.product_id ? String(decision.product_id) : ''} onValueChange={(value) => { const option = productOptions.find((item) => item.id === Number(value)); updateDecision(row.row_key, { product_id: Number(value), requires_lot: option?.product_type?.requires_lot, batch_mode: option?.product_type?.requires_lot ? 'new' : undefined, product_batch_id: undefined, lot_number: undefined, expiration_date: undefined }) }}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar producto activo" /></SelectTrigger><SelectContent>{productOptions.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name} · {item.barcode}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                      {decision?.resolution === 'new_product' && <NewProductFields decision={decision} row={row} taxes={taxes} update={(values) => updateDecision(row.row_key, values)} />}
                      {decision?.resolution === 'non_inventory' && <p className="mt-3 rounded-md bg-blue/70 p-3 text-sm text-blue-300/80">La partida se conservará fiscalmente sin crear producto ni afectar inventario.</p>}
                      {decision?.resolution !== 'non_inventory' && requiresLot && <XmlLotFields decision={decision} productId={decision.product_id} sucursalId={sucursalId} today={today} update={(values) => updateDecision(row.row_key, values)} />}
                    </div>
                  )
                })}
              </div>
            )}

            {preview.rows.filter((row) => row.status === 'existing_product' && row.resolved_product?.product_type?.requires_lot).map((row) => {
              const decision = decisions[row.row_key]
              return <div key={row.row_key} className="rounded-lg border border-slate-200 p-4"><p className="font-medium">Lote de {row.resolved_product?.name}</p><XmlLotFields decision={decision} productId={row.resolved_product?.id} sucursalId={sucursalId} today={today} update={(values) => updateDecision(row.row_key, values)} /></div>
            })}

            {preview.rows.some((row) => row.cost_comparison.change_percentage !== null && Math.abs(row.cost_comparison.change_percentage) > 10) && (
             <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-4">
              <div className="flex items-start gap-3">
                <TrendingUp className="mt-0.5 h-5 w-5 text-yellow-500" />

                <div>
                  <h4 className="font-medium text-yellow-400">
                    Cambios significativos detectados
                  </h4>

                  <p className="mt-1 text-sm text-card-foreground">
                    Algunos productos tienen cambios superiores al 10% respecto a su
                    costo anterior. Revisa los valores antes de confirmar.
                  </p>
                </div>
              </div>
            </div>
            )}
            </div>
          )}

          {error && <Alert text={error} danger />}
        </div>
        <DialogFooter className="shrink-0 gap-2">
          {step === 2 && <Button type="button" variant="outline" onClick={() => setStep(1)} disabled={submitting || analyzing}><ArrowLeft className="mr-2 h-4 w-4" />Volver</Button>}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}><X className="mr-2 h-4 w-4" />Cancelar</Button>
          {step === 1 ? <Button type="button" disabled={!canContinue || branchesQuery.isLoading || branchesQuery.isError} onClick={() => setStep(2)}>Siguiente</Button> : <Button type="button" disabled={!canContinue || invalidRows || submitting || analyzing || !destination} onClick={() => void confirm()}>{submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Confirmar ingreso</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NewProductFields({ decision, row, taxes, update }: { decision: LocalDecision; row: PurchaseXmlPreviewRow; taxes: TaxItem[]; update: (values: Partial<LocalDecision>) => void }) {
  return <div className="mt-4 grid min-w-0 gap-3 rounded-md border border-blue-400/50 bg-card p-4 md:grid-cols-2">
    <Field label="Nombre *"><Input value={decision.name || ''} onChange={(event) => update({ name: event.target.value })} /></Field>
    <Field label="Código de barras *"><Input value={decision.barcode || ''} onChange={(event) => update({ barcode: event.target.value })} /></Field>
    <Field label="Descripción *"><Input value={decision.description || ''} onChange={(event) => update({ description: event.target.value })} /></Field>
    <Field label="Clave SAT (opcional)"><SatKeySelect value={decision.sat_product_key_code} selectedName={decision.sat_product_key_name} onChange={(code, name) => update({ sat_product_key_code: code, sat_product_key_name: name })} onClear={() => update({ sat_product_key_code: undefined, sat_product_key_name: undefined })} /></Field>
    <Field label="Categoría *"><CatalogSelect catalog="categories" value={decision.category_id ? String(decision.category_id) : ''} selectedItem={row.resolved_catalogs.category || undefined} onChange={(value) => update({ category_id: Number(value) || undefined })} /></Field>
    <Field label="Departamento *"><CatalogSelect catalog="departments" value={decision.department_id ? String(decision.department_id) : ''} selectedItem={row.resolved_catalogs.department || undefined} onChange={(value) => update({ department_id: Number(value) || undefined })} /></Field>
    <Field label="Tipo de producto *"><CatalogSelect catalog="product_types" returnObject value={decision.product_type_id ? String(decision.product_type_id) : ''} selectedItem={row.resolved_catalogs.product_type || undefined} onChange={(value: unknown) => { const item = value as { id?: number | string; requires_lot?: boolean } | null; const requiresLot = Boolean(item?.requires_lot); update({ product_type_id: item?.id ? Number(item.id) : undefined, requires_lot: requiresLot, batch_mode: requiresLot ? 'new' : undefined, product_batch_id: undefined, lot_number: undefined, expiration_date: undefined }) }} /></Field>
    <Field label="Unidad *"><CatalogSelect catalog="units" value={decision.unit_id ? String(decision.unit_id) : ''} selectedItem={row.resolved_catalogs.unit || undefined} onChange={(value) => update({ unit_id: Number(value) || undefined })} /></Field>
    <Field label="Impuesto local (opcional)"><Select value={decision.tax_id ? String(decision.tax_id) : ''} onValueChange={(value) => update({ tax_id: Number(value) })}><SelectTrigger><SelectValue placeholder="Sin seleccionar" /></SelectTrigger><SelectContent>{taxes.map((tax) => <SelectItem key={tax.id} value={String(tax.id)}>{tax.name} · {tax.percentage}%</SelectItem>)}</SelectContent></Select></Field>
    <Field label="Utilidad % (opcional)"><Input type="number" min="0" step="0.01" value={decision.profit_percentage ?? ''} onChange={(event) => update({ profit_percentage: event.target.value === '' ? undefined : Number(event.target.value) })} /></Field>
    <p className="text-xs text-card-description md:col-span-2">Cantidad {row.quantity} · costo neto {money(row.net_unit_cost)}. El producto sólo quedará activo si impuesto y utilidad están completos.</p>
  </div>
}

function XmlLotFields({ decision, productId, sucursalId, today, update }: {
  decision: LocalDecision
  productId?: number
  sucursalId: string
  today: string
  update: (values: Partial<LocalDecision>) => void
}) {
  return <div className="mt-3 grid gap-3 md:grid-cols-2">
    <Field label="Destino del lote *">
      <Select value={decision.batch_mode || 'new'} onValueChange={(value: 'new' | 'existing') => update({ batch_mode: value, product_batch_id: undefined, lot_number: undefined, expiration_date: undefined })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="new">Lote nuevo</SelectItem>{productId && <SelectItem value="existing">Lote existente</SelectItem>}</SelectContent>
      </Select>
    </Field>
    {decision.batch_mode === 'existing' ? <Field label="Lote existente *">
      <PurchaseBatchSelect productId={productId} sucursalId={Number(sucursalId) || undefined} value={decision.product_batch_id} onChange={(productBatchId) => update({ product_batch_id: productBatchId })} />
    </Field> : <>
      <Field label="Número de lote *"><Input value={decision.lot_number || ''} onChange={(event) => update({ lot_number: event.target.value })} /></Field>
      <Field label="Caducidad *"><Input type="date" min={today} value={decision.expiration_date || ''} onChange={(event) => update({ expiration_date: event.target.value })} /></Field>
    </>}
  </div>
}

function Field({ label, children }: { label: string; children: React.ReactNode }) { return <div className="min-w-0 space-y-1"><label className="text-sm font-medium text-card-foreground">{label}</label>{children}</div> }
function Info({ label, value, className }: { label: string; value: string; className?: string }) { return <div className={`flex flex-col gap-0.5 ${className || ''}`}><p className="text-xs text-card-description">{label}</p> <p className="break-words text-sm font-medium text-card-foreground">{value}</p></div> }
function ReviewCount({ label, value }: { label: string; value: number }) {return (<div><p className="text-card-description">{label}:</p><p className="font-semibold text-card-foreground">{value} producto{value === 1 ? '' : 's'}</p></div>)}
function Alert({ text, danger = false }: { text: string; danger?: boolean }) { return <div className={`mt-3 flex gap-2 rounded-md border p-3 text-sm ${danger ? 'border-red-200 bg-red-50 text-red-800' : 'border-blue-200 bg-blue-50 text-amber-800'}`}><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{text}</div> }
function WarningCheck({ code, text, checked, onChange }: { code: string; text: string; checked: boolean; onChange: (checked: boolean) => void }) { return <label className="flex cursor-pointer gap-3 rounded-md border border-blue-400/50 bg-blue-500/10 p-3 text-sm text-blue-300"><input aria-label={code} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="mt-1" /><span>{text}</span></label> }
function CostChange({ row }: { row: PurchaseXmlPreviewRow }) {
  const change = row.cost_comparison.change_percentage
  if (change === null) return <Badge variant="outline" className="border-border text-card-foreground">Sin costo anterior</Badge>
  if (change > 0) return <Badge className="bg-red-100 text-red-700"><ArrowUp className="mr-1 h-3 w-3" />+{change.toFixed(2)}%</Badge>
  if (change < 0) return <Badge className="bg-emerald-100 text-emerald-700"><ArrowDown className="mr-1 h-3 w-3" />{change.toFixed(2)}%</Badge>
  return <Badge variant="outline"><Minus className="mr-1 h-3 w-3" />Sin cambio</Badge>
}
